import React, { useState, useEffect, useRef } from "react";
import { Input } from "@coreai/component-library";
import { useTheme } from "@emotion/react";
import AddProjectDialog from "./AlertDialogAddProject";
import {
  listProjects,
  ProjectsModel,
} from "../../../../services/projectsService";
import { useLanguage } from "LanguageContext";

interface ProjectTextFieldProps {
  setProject: (project: ProjectsModel | null) => void;
  projectId: string | null;
}

const ProjectTextField: React.FC<ProjectTextFieldProps> = (
  props: ProjectTextFieldProps
) => {
  const { setProject } = props;
  const { t } = useLanguage();
  const theme = useTheme();
  const [inputValue, setInputValue] = useState("");  
  const [chosenProject, setChosenProject] = useState<ProjectsModel | null>(
null  );
  const [projects, setProjects] = useState<ProjectsModel[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<ProjectsModel[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAlertDialog, setShowAlertDialog] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [autoSelectProject, setAutoSelectProject] = useState<string>("");

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const errorRef = useRef<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(0);
 // const listRef = useRef<HTMLUListElement>(null);
 const listRef = useRef<(HTMLLIElement | null)[]>([]);

  useEffect(() => {
    async function fetchData() {
      const projectsList = await listProjects({ sortingColumns: {} });
      if (!projectsList?.results) return;

      setProjects((prevSuggestions) => {
        const newSuggestions = projectsList?.results?.filter(
          (result: any) =>
            !prevSuggestions.some(
              (suggestion) => suggestion.name === result.name
            )
        );

        return [
          ...prevSuggestions,
          ...newSuggestions.map((result: any) => ({
            advertiser: result.advertiser,
            id: result.id,
            name: result.name,
            total_analysis: result.total_analysis ?? 0,
          })),
        ];
      });
    }

    if (!showAlertDialog) {
      setTimeout(()=>{
        fetchData();
      },100)
    }
  }, [showAlertDialog]);

  useEffect(()=>{
    setFilteredProjects(projects);
  },[projects])
  

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setChosenProject(null);
    setInputValue(value);

    if (value) {
      const searchValue = value.toLowerCase().replace(/\s*-\s*/g, " ").trim();
    
      const newFilteredProjects = projects.filter((project) => {
        const combined = `${project.name} ${project.advertiser}`
          .toLowerCase()
          .replace(/\s*-\s*/g, " ")
          .trim();
        return combined.includes(searchValue);
      });
    
      setFilteredProjects(newFilteredProjects);
    } else {
      setFilteredProjects(projects);
    }

    setShowDropdown(true);
  };

  const handleFocus = () => {
    if (!inputValue) {
      setFilteredProjects(projects);
    }
    errorRef.current = false;
    setErrorMsg("");
    setShowDropdown(true);
  };

  const handleSuggestionClick = (suggestion: ProjectsModel) => {
    setChosenProject(suggestion);
    setShowDropdown(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setShowDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  useEffect(() => {
    setProject(chosenProject);
  }, [chosenProject, setProject]);

  const normalized = (str: string) =>
    str.toLowerCase().replace(/\s*-\s*/g, " ").trim();

  const hasMatchingProject = filteredProjects?.some(
    (proj) =>
      normalized(`${proj.name} ${proj.advertiser}`) === normalized(inputValue)
  );
  useEffect(() =>{
    if(hasMatchingProject) setProject(filteredProjects?.[0]);
  },[setProject, filteredProjects, hasMatchingProject]);

  useEffect(() => {
    if (!showDropdown) {
  
      if (
        chosenProject === null &&
        (inputValue !== "" &&
        !hasMatchingProject)
      ) {
        errorRef.current = true;
      } else {
        errorRef.current = false;
      }
  
      if (inputValue === "") {
        errorRef.current = false;
      }
    }
  
    if (errorRef.current) {
      setErrorMsg(t("analysis_project_error"));
      setProject(null);
    }
  }, [chosenProject, inputValue, filteredProjects, showDropdown, t, setProject, hasMatchingProject]);
  

  useEffect(() => {
   if(props.projectId){
      projects.find((proj)=>{
        if(proj.id === props.projectId){
          setChosenProject(proj)
        }
      })
   }
  }, [props.projectId,projects]);

  useEffect(() => {
    if(autoSelectProject){
       projects.find((proj)=>{
         if(proj?.name === autoSelectProject){
           setChosenProject(proj)
         }
       })
    }
   }, [autoSelectProject, projects]);

  const handleAddNewProjectClick = () => {
    setShowAlertDialog(true);
    setShowDropdown(false);
  };

  const updateProject = (setProject: string) => {
    setAutoSelectProject(setProject);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown) return;
     let keyName = e?.key?.toLocaleLowerCase();
    if (keyName === "arrowdown") {
      e.preventDefault();
       setActiveIndex((prev) => (prev + 1) % filteredProjects?.length);       
    } else if (keyName === "escape") {
        e.preventDefault();
        setShowDropdown(false);
     } else if (keyName === "arrowup") {
      e.preventDefault();
       setActiveIndex((prev) => (prev - 1 + filteredProjects?.length) % filteredProjects?.length);
    } else if (keyName === "enter") {
      if (activeIndex >= 0) {
        setChosenProject(filteredProjects[activeIndex]);
        setShowDropdown(false);
      }
    }
  };
useEffect(() => {
    if (activeIndex >= 0 && listRef.current[activeIndex]) {
      listRef.current[activeIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [activeIndex]);

  useEffect(() => {
    const handleKeyDown = (event: any) => {
      if (event.key === "Escape") {
        setShowDropdown(false);
        // Close any other popups or reset their states here
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
    }, []);

  const dropdownStyles = {
    position: "absolute",
    backgroundColor: "white",
    border: `1px solid ${theme.colors.strokeGray}`,
    borderTop: "none",
    borderRadius: `0 0 ${theme.radius.xs} ${theme.radius.xs}`,
    width: "100%",
    zIndex: 1000,
    display: "flex",
    flexDirection: "column",
    boxShadow: theme.shadows.medium,
  } as React.CSSProperties;

  const suggestionsContainerStyles = {
    maxHeight: "150px",
    overflowY: "auto",
  } as React.CSSProperties;

  const dropdownItemStyles = {
    padding: theme.spacing.md,
    fontFamily: theme.typography.bodyMedium.fontFamily,
    fontSize: theme.typography.bodyMedium.fontSize,
    fontWeight: theme.typography.bodyMedium.fontWeight,
    lineHeight: theme.typography.bodyMedium.lineHeight,
    color: theme.colors.textMedium,
    cursor: "pointer",
  } as React.CSSProperties;

  const dropdownItemHoverStyles = {
    backgroundColor: "#f0f0f0",
    padding:'8px',
  };

  const addNewProjectStyles = {
    padding: theme.spacing.md,
    borderRadius: `0 0 ${theme.radius.xs} ${theme.radius.xs} `,
    cursor: "pointer",
    color: theme.colors.primary,
    borderBottom: theme.radius.xs,
    backgroundColor: theme.colors.white,
  } as React.CSSProperties;

  const containerStyles = {
    position: "relative",
    width: "100%",
  } as React.CSSProperties;
  return (
    <>
      <div style={containerStyles} ref={dropdownRef}>
        <Input
          type="text"
          label={t("analyse_form_label_project")}
          value={
            chosenProject
              ? `${chosenProject?.name} - ${chosenProject?.advertiser}`
              : inputValue
          }
          onChange={handleInputChange}
          onFocus={handleFocus}
          autoComplete="off"
          error={errorRef.current}
          errorText={errorRef.current ? errorMsg: ""}
          name="project"
          required
        />

        {showDropdown && (
          <div style={dropdownStyles}    
             onKeyDown={handleKeyDown}
              tabIndex={0}
          >
            <ul style={suggestionsContainerStyles}   >
              {filteredProjects
                .sort((a, b) => {
                  if (a.name !== b.name) {
                    return a.name.localeCompare(b.name);
                  }
                  return a.advertiser.localeCompare(b.advertiser);
                })
                .map((suggestion, index) => (
                  <li
                    ref={(el) => (listRef.current[index] = el)}
                    key={suggestion.id}
                    style={index === activeIndex ? dropdownItemHoverStyles : dropdownItemStyles}
                    onClick={() => handleSuggestionClick(suggestion)}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        dropdownItemHoverStyles.backgroundColor)
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                 {suggestion.name} - {suggestion.advertiser}
                  </li>
              ))}
            </ul>

            <div
              style={addNewProjectStyles}
              onClick={handleAddNewProjectClick}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor =
                  dropdownItemHoverStyles.backgroundColor)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
            >
              {t("add_project_from_dropdown")}
            </div>
          </div>
        )}
      </div>
      <AddProjectDialog
        open={showAlertDialog}
        onClose={() => setShowAlertDialog(false)}
        suggestions={projects}
        saveProject={updateProject}
        projectInputValue={inputValue}
      />
    </>
  );
};

export default ProjectTextField;
